import {
  Component,
  ElementRef,
  inject,
  OnInit,
  OnDestroy,
  ViewChild,
  AfterViewInit,
} from "@angular/core";
import { firstValueFrom, Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import {
  RnFolderSearch,
  RnObjectRNResponseRelay,
  RnOrganizationContainerCreate,
  RnOrganizationFolderRename,
  RnRNOrderHeader,
  RnRNOrgContainer,
  RnUsersVM,
} from "../../../services/rnapi2-service/models/models";
import { OrganizationService } from "../../../services/rnapi2-service/apis/api";
import { CreateRenameFolderConfig } from "./create-rename-folder-config";
import { HttpErrorResponse } from "@angular/common/http";
import { RnErrorHandlerService } from "../../../services/rn-error-handler/rn-error-handler.service";
import { NotificationDialogService } from "../../../services/notificationDialog/notification-dialog.service";
import { OrgRenameUserLoggedInConfig } from "../rename-error/rename-error-config";
import { ToasterService, ToastType } from '@rn-platform/frontend-shared-data-access-toaster';
import { ModalComponent } from "@rn-platform/frontend-shared-ui-common";
import { CreateRenameFolderModalService } from "./create-rename-folder-modal.service";
import { NgForm } from "@angular/forms";

@Component({
    selector: "app-create-rename-folder",
    templateUrl: "./create-rename-folder.component.html",
    styleUrls: ["./create-rename-folder.component.scss"],
    standalone: false
})
export class CreateRenameFolderComponent implements OnInit, OnDestroy, AfterViewInit {
  private orgService = inject(OrganizationService);
  private rnErrorHandlerService = inject(RnErrorHandlerService);
  private toasterService = inject(ToasterService);
  private notificationDialogService = inject(NotificationDialogService);


  @ViewChild("containerForm") public userFrm: NgForm;
  @ViewChild("folderNameFocus")  public folderNameInputElement!: ElementRef<HTMLInputElement>;
  @ViewChild("modalRef") modalComponent: ModalComponent;

  private subscriptions: Subscription[] = [];
  private readonly searchTextSubject: Subject<string> = new Subject<string>();
  private readonly createRenameFolderModalService = inject(CreateRenameFolderModalService);

  private _submitDisabled: boolean = true;
  public get submitDisabled(): boolean {
    return this._submitDisabled;
  }

  public readonly maxLength: number = 64;
  public readonly minLength: number = 1;
  public configuration: CreateRenameFolderConfig = new CreateRenameFolderConfig();
  public working : boolean = false;
  public pendingFolderName: string; 

  //Error States:
  public hasAnyError: boolean = false; 
  public hasWhiteSpaceError: boolean = false;
  public hasRequiredError: boolean = false;
  public hasMaxLengthError: boolean = false;
  public hasPatternError: boolean = false;
  public hasNameInUseError: boolean = false;

  ngOnInit(): void {
    // Input Handling Subscription
    const searchTextSub = this.searchTextSubject
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe(async (value) => {
        this.pendingFolderName = value;
        await this.validateAndUpdateControls();
    });
    this.subscriptions.push(searchTextSub);
    //
    // Modal Service Subscription
    const modalSub = this.createRenameFolderModalService.modalState$.subscribe(data => {
      if(!data) return;
      if (data?.config) {
        if (data?.isOpen) {
            this.open();
        }
        else {
          this.close();
        }
        this.configuration = data?.config;
         if (this.configuration) {
          this.pendingFolderName = this.configuration.currentName;
        }        
      }
      this.configuration = data.config;
    });
    this.subscriptions.push(modalSub);
    //
  }
  
  ngAfterViewInit(): void {
    this.folderNameInputElement?.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  onInputChange(value: string) {
    this.disableSubmit();
    this.working = true;
    this.searchTextSubject.next(value);
  }

  async validateAndUpdateControls() {
    if(await this.folderNameIsValidForSubmit()){
      this.userFrm.controls["folderName"].setErrors(null);
      this.hasAnyError = false;
      this.enableSubmit();
    }
    this.working = false;
  }

  async submit(): Promise<void> {
    if (await this.folderNameIsValidForSubmit()){
      if (this.configuration.isEdit) {
        await this.submitEditFolderName();
      } else {
        await this.createOrganizationContainer();
      }
    }
  }

  async createOrganizationContainer(): Promise<void> {
    this.working = true;
    this.disableSubmit();
    const payload = new RnOrganizationContainerCreate();
    payload.Header = new RnRNOrderHeader();
    payload.Header.Order_XID = "-1";
    payload.Header.Order_Source = "AppHub";
    payload.Header.Order_Date = new Date().toUTCString();
    payload.Header.SalesAgent_TAG = this.configuration.loggedInUserID.toString();
    payload.OrgContainer = new RnRNOrgContainer();
    payload.OrgContainer.ParentFolderID = this.configuration.ParentFolderID;
    payload.OrgContainer.Parent_ID = this.configuration.ParentID;
    payload.OrgContainer.Display_Name = this.pendingFolderName.trim();

    try {
      const r = await firstValueFrom(
        this.orgService.apiV2OrganizationsCreateorgcontainerPost(payload, "response")
      );
      if (r.body.Success) {
        const message = 'Folder created successfully';
        const id = 'apphub-organization-tree-new-folder-success-toast';
        this.toasterService.addToast(ToastType.Success_, message, message, id, id);
        this.close();
        globalThis.location.reload();
      }
    } catch (error) {
      const id = 'apphub-create-renamed-folder-create-org-container-error-toast';
      const message = 'Cannot create a new folder at this time';
      this.toasterService.addToast(ToastType.Error_, message, message, id, id);
      this.rnErrorHandlerService.handleError("", error.Messages);
      this.working = false;
    }
  }


  async submitEditFolderName(): Promise<void> {
    this.working = true;
    this.disableSubmit();
    const payload = new RnOrganizationFolderRename();
    payload.AffectedOrganizationId = this.configuration.ParentID;
    payload.OrganizationID = this.configuration.ParentID;
    payload.FolderID = this.configuration.ParentFolderID;
    payload.UpdatedName = this.pendingFolderName.trim();
    try {
      const r = await firstValueFrom(
        this.orgService.apiV2OrganizationsFolderRenamePost(payload, "response")
      );
      if (r.body.Success) {
        this.close();
        globalThis.location.reload();
      }
    } catch (error) {
      const errResp = error as HttpErrorResponse;
      if (errResp?.error) {
        const rnErr = errResp.error as RnObjectRNResponseRelay;
        if (rnErr?.data) {
          const loggedInUsers = rnErr.data as Array<RnUsersVM>;
          if (loggedInUsers && loggedInUsers.length > 0) {
            const errorMsgConfig = new OrgRenameUserLoggedInConfig();
            errorMsgConfig.type = "folder";
            errorMsgConfig.users = loggedInUsers;
            this.notificationDialogService.ShowRenameFolderError(errorMsgConfig);
            this.working = false;
            return;
          }
        }
        this.rnErrorHandlerService.handleError(
          "An error occurred attempting to change the top level folder name.",
          rnErr?.Messages,
        );
      }
      const id = 'apphub-create-renamed-folder-rename-error-toast';
      const message = 'There was an error renaming the folder.';
      this.toasterService.addToast(ToastType.Error_, message, message, id, id);
      this.working = false;
    }
  }
  open(): void {
    this.modalComponent?.open();
  }

  close(): void {
    this.modalComponent?.close();
    this.resetState();
  }


  async folderNameIsValidForSubmit(): Promise<boolean> {  
    if (this.inputHasCharacterErrors() || await this.folderNameIsDuplicate()) {
      this.hasAnyError = true;
      return false;
    }
    if(this.configuration.isEdit){
      if( !this.editedNameHasChanged()){
        return false;
      }

    }
    this.hasAnyError = false;
    return true;
  }

  async folderNameIsDuplicate(): Promise<boolean> {
    const payload = new RnFolderSearch();
    payload.search = this.pendingFolderName.trim();
    if (this.configuration.isEdit) {
      payload.ParentFolderId = -1;
      payload.Id = this.configuration.ParentFolderID.toString();
    } else {
      payload.ParentFolderId = this.configuration.ParentFolderID;
      payload.Id = "-1";
    }
    return new Promise((resolve) => {
        this.orgService.apiV2OrganizationsIsfoldernameuniquePost(payload, "response")
        .subscribe((r) => {
          if (r.body.Success && !r.body.data) {
            this.userFrm.controls["folderName"].setErrors({ folderNameInUseError: true });
            this.userFrm.controls["folderName"].setErrors({ pendingvalidation: true });
            this.hasNameInUseError = true;
            this.disableSubmit();
            resolve(true);
          }
          else{
            this.hasNameInUseError = false;
            this.userFrm.controls["folderName"].setErrors({ folderNameInUseError: false });
            this.userFrm.controls["folderName"].setErrors({ pendingvalidation: false });
            resolve(false);
          }

        });
      });
  }

  inputHasCharacterErrors(): boolean {
      this.hasPatternError = this.inputHasPatternError();
      this.hasRequiredError = this.inputHasMinLengthError();
      this.hasMaxLengthError = this.inputHasMaxLengthError();
      return this.hasPatternError || this.hasRequiredError || this.hasMaxLengthError;
  }

  inputHasPatternError(): boolean { 
    const INVALID_CHARS = new Set([
        '<', '>', ':', '"', '/', '\\', '|', '?', '*', ',', '&', '\''
      ]);
    const INVALID_TRAILING_CHARS = new Set(['.', ' ']);
    const input = this.pendingFolderName;
    if (!input || input.length === 0 || typeof input !== 'string') {
      this.hasRequiredError = true;
      return false;
    }
    for (const char of INVALID_CHARS) {
      if (input.includes(char)) {
        this.hasPatternError = true;
        this.disableSubmit();
        return true;
      }
    } 
    if (INVALID_TRAILING_CHARS.has(input.at(-1))) {
      this.hasPatternError = true;
      this.disableSubmit();
      return true;
    }
    this.hasPatternError = false;
    return false;
  }

  inputHasMinLengthError(): boolean {
    if (!this.pendingFolderName || this.pendingFolderName.trim().length < 1) {
      this.hasRequiredError = true;
      this.disableSubmit();
      return true;
    }
    this.hasRequiredError = false;
    return false;
  }

  inputHasMaxLengthError(): boolean {
    if (this.pendingFolderName && this.pendingFolderName.length > this.maxLength) {
      this.hasMaxLengthError = true;
      this.disableSubmit();
      return true;
    }
    this.hasMaxLengthError = false;
    return false;
  }

  private enableSubmit(): void {
    if(!this.hasAnyError)
      this._submitDisabled = false;
  }

  public disableSubmit() {
    this._submitDisabled = true;
  }

  resetState() {
    this.configuration.clear();
    this.disableSubmit();
    this.working = false;
    this.hasNameInUseError = false;
    if (this.userFrm?.controls["folderName"]) {
      this.hasPatternError = false;
      this.userFrm.controls["folderName"].markAsPristine();
      this.userFrm.controls["folderName"].markAsUntouched();
      this.userFrm.resetForm();
    }
  }

  editedNameHasChanged() : boolean {
    if(this.configuration.isEdit){
        if(this.pendingFolderName  === this.configuration.currentName){
          this.disableSubmit();
          return false;
        }
        return true;
    }
    this.disableSubmit();
    return false;
  }
}
